import { useQuery, useReactiveVar } from '@apollo/client';
import { Checkbox, Stack, Toggle } from '@fluentui/react';
import { DropdownFilter, FilterArrayType } from 'common/components/Filters';
import { getUpdatedFilterArray } from 'common/components/Filters/utils';
import {
  ColumnData,
  SearchBar,
  SearchBarProps,
} from 'common/components/SearchBar';
import { formatDropdownOptions } from 'common/utils';
import React, { useState } from 'react';
import { PurchaseOrderFiltersData } from './__generated__/PurchaseOrderFiltersData';
import { useStyles } from './index.styles';
import { setPOListViewOptions } from '..';
import { globalMode } from 'utility/cache/ui';
import { ToggleFilter } from 'common/components/Filters/ToggleFilter';
import PURCHASE_ORDER_FILTERS_DATA from './PurchaseOrderFiltersData.graphql';
import { setPOListSupplierViewOptions } from 'purchaseOrder/view/FormView/SupplierModal/SupplierModalPOListView';

interface POSearchBarProps extends SearchBarProps {
  onHintsViewToggle?: () => void;
  columns: ColumnData[];
  onFilterChange: (filterArray: FilterArrayType[]) => void;
  onToggleVisibility?: (columns: ColumnData[]) => void;
  isSigning: boolean;
  isPOSupplierList?: boolean;
}

export const POSearchBar: React.FC<POSearchBarProps> = ({
  columns,
  onFilterChange,
  onEnterPress,
  hintsAvailable,
  onRefresh,
  searchInputPlaceholderText,
  onToggleVisibility,
  onHintsViewToggle,
  urlParams,
  onGlobalToggle,
  isSigning,
  isPOSupplierList = false,
}) => {
  const globalState = useReactiveVar(globalMode);
  const styles = useStyles();
  const [filterTypes, setFilterTypes] = useState<FilterArrayType[]>([]);
  const POListViewOptions = useReactiveVar(setPOListViewOptions);
  const POListSupplierViewOptions = useReactiveVar(
    setPOListSupplierViewOptions
  );

  const { data: purchaseOrderFiltersData } = useQuery<PurchaseOrderFiltersData>(
    PURCHASE_ORDER_FILTERS_DATA
  );

  const supplierTypeOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.availableSuppliers?.nodes,
    {
      getKey: (item) => item.id || '',
      getText: (item) => item._fullName || '',
      includeAll: true,
    }
  );
  const purchaseOrderTypeOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.companyPurchaseOrderTypes?.nodes,
    {
      getKey: (item) => item.purchaseOrderTypeId || '',
      getText: (item) => item.purchaseOrderType || '',
      includeAll: true,
    }
  );

  const departmentOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.companyDepartments?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: true,
    }
  );

  const businessUnitOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.companyBusinessUnits?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: true,
    }
  );

  const currencyOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.companyCurrencies?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: true,
    }
  );

  const statusOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.purchaseOrderStatusTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.statusType || '',
    }
  );
  const createdByOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.purchaseOrderUsers?.nodes,
    {
      getKey: (item) => item.userId || '',
      getText: (item) => item.userName || '',
      includeAll: true,
    }
  );
  const accountHistoryOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.purchaseOrderAccountingHistories?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.exportInfo ?? '',
      includeAll: true,
      multiSelect: true,
    }
  );
  const onOptionChange = (filterdata: FilterArrayType) => {
    const availableFilters = [...filterTypes];
    const newFilters = getUpdatedFilterArray(filterdata, availableFilters);
    onFilterChange?.(newFilters);
    setFilterTypes(newFilters);
  };

  return (
    <SearchBar
      urlParams={urlParams}
      onFilterClear={() => {
        if (filterTypes.length) onFilterChange?.([]);
        setFilterTypes([]);
      }}
      columns={columns}
      searchInputPlaceholderText={searchInputPlaceholderText}
      onEnterPress={onEnterPress}
      onRefresh={onRefresh}
      onGlobalToggle={onGlobalToggle}
      hintsAvailable={hintsAvailable}
      onHintsViewToggle={onHintsViewToggle}
      onToggleVisibility={onToggleVisibility}
      renderActions={
        <Stack
          className={styles.actionsWrapperStyle}
          horizontalAlign="center"
          verticalAlign="center"
          horizontal
          tokens={{ childrenGap: 25 }}
        >
          <Toggle
            label="Signing"
            disabled={globalState}
            inlineLabel
            onText="Accounting Entry"
            offText="Accounting Entry"
            onChange={(event, checked) => {
              if (event)
                !isPOSupplierList
                  ? setPOListViewOptions({
                      ...POListViewOptions,
                      isSigning: !checked,
                      isHistory:
                        POListViewOptions.isHistory && !checked
                          ? false
                          : POListViewOptions.isHistory,
                    })
                  : setPOListSupplierViewOptions({
                      ...POListSupplierViewOptions,
                      isSigning: !checked,
                      isHistory:
                        POListSupplierViewOptions.isHistory && !checked
                          ? false
                          : POListSupplierViewOptions.isHistory,
                    });
            }}
            checked={
              isPOSupplierList
                ? !POListSupplierViewOptions.isSigning
                : !POListViewOptions.isSigning
            }
            className={styles.signingAccountingToggle}
            styles={{
              root: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              },

              container: {
                display: 'flex',
                alignItems: 'center',
              },
              label: {
                wordBreak: 'keep-all',
              },
            }}
          />

          {!POListViewOptions.isSigning && (
            <Checkbox
              // disabled={disabled}
              // styles={checkBoxStyle}
              label="History"
              checked={
                isPOSupplierList
                  ? POListSupplierViewOptions.isHistory
                  : POListViewOptions.isHistory
              }
              onChange={(event, checked) => {
                if (event) {
                  !isPOSupplierList
                    ? setPOListViewOptions({
                        ...POListViewOptions,
                        isHistory: !!checked,
                      })
                    : setPOListSupplierViewOptions({
                        ...POListSupplierViewOptions,
                        isHistory: !!checked,
                      });
                }
              }}
            />
          )}
        </Stack>
      }
    >
      <Stack
        verticalAlign="center"
        horizontal
        tokens={{ childrenGap: 20, padding: '0px 0px 0px 20px' }}
      >
        {!isPOSupplierList && (
          <DropdownFilter
            filterKey={'filterSupplierId'}
            options={supplierTypeOptions || []}
            placeholder={'Supplier'}
            onOptionChange={onOptionChange}
          />
        )}
        <DropdownFilter
          filterKey={'purchaseOrderTypeId'}
          options={purchaseOrderTypeOptions || []}
          placeholder={'Type'}
          onOptionChange={onOptionChange}
        />
        <DropdownFilter
          filterKey={'departmentId'}
          options={departmentOptions || []}
          placeholder={'Department'}
          onOptionChange={onOptionChange}
        />
        <DropdownFilter
          filterKey={'businessUnitId'}
          options={businessUnitOptions || []}
          placeholder={'Business Unit'}
          onOptionChange={onOptionChange}
        />
        <DropdownFilter
          filterKey={'currencyId'}
          options={currencyOptions || []}
          placeholder={'Currency'}
          onOptionChange={onOptionChange}
        />
        <DropdownFilter
          filterKey="filterMultiStatusTypes"
          options={statusOptions}
          placeholder="Status"
          onOptionChange={onOptionChange}
          multiSelect
          maxWidth={150}
        />
        <DropdownFilter
          filterKey="_createdByUserid"
          options={createdByOptions}
          placeholder="Created By"
          onOptionChange={onOptionChange}
        />
        <DropdownFilter
          filterKey={'_accountingExportCycleId'}
          options={accountHistoryOptions}
          placeholder={'Exported'}
          disabled={isSigning}
          onOptionChange={onOptionChange}
          width={150}
          maxHeight={400}
        />
        <Stack verticalAlign="end" style={{ marginTop: 5 }}>
          <ToggleFilter
            placeholder="New"
            disabled={isSigning}
            inlineLabel
            filterKey="isFilterUnprocessedDocuments"
            onOptionChange={onOptionChange}
          />
        </Stack>
      </Stack>
    </SearchBar>
  );
};
